import React, { useState } from 'react';
import { Card } from './ui/card';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRevealOnScroll } from './scroll-animations';

interface FAQItem {
  question: string;
  answer: string;
  category: 'general' | 'technical' | 'pricing' | 'features';
}

const faqs: FAQItem[] = [
  {
    question: 'How accurate is the speech recognition?',
    answer: 'Our AI-powered engine achieves 99.9% accuracy for native speakers in quiet environments. It handles accents, technical jargon, and even learns your speaking patterns over time for even better results.',
    category: 'technical',
  },
  {
    question: 'Does it work offline?',
    answer: 'Yes! Once installed, SpeakFlow works completely offline. No internet connection required. Your voice data never leaves your computer, ensuring complete privacy and security.',
    category: 'features',
  },
  {
    question: 'What languages are supported?',
    answer: 'We support 50+ languages including English, Spanish, French, German, Chinese, Japanese, and more. Each language includes multiple accent variants and dialect recognition.',
    category: 'features',
  },
  {
    question: 'Can I use it with my existing software?',
    answer: 'Absolutely! SpeakFlow works with any Windows application - Microsoft Office, Google Docs, VS Code, emails, chat apps, and more. It integrates seamlessly wherever you can type.',
    category: 'technical',
  },
  {
    question: 'Is there a free trial?',
    answer: 'Yes! We offer a 7-day free trial with full features. No credit card required. You can test drive everything before making a decision.',
    category: 'pricing',
  },
  {
    question: 'What\'s your refund policy?',
    answer: 'We offer a 30-day money-back guarantee. If you\'re not completely satisfied, contact support for a full refund. No questions asked.',
    category: 'pricing',
  },
  {
    question: 'How do voice commands work?',
    answer: 'Simply speak naturally! Say "period" for a full stop, "new line" to start a new line, or "select last sentence" to edit. We have 100+ voice commands for formatting, editing, and navigation.',
    category: 'features',
  },
  {
    question: 'Can multiple people use one license?',
    answer: 'Our individual license covers one user on up to 2 devices. For teams, we offer special pricing with our Lifetime plan that includes 5 seats.',
    category: 'pricing',
  },
  {
    question: 'How much time will I really save?',
    answer: 'Most users report saving 2-4 hours daily. You can speak at 150-200 words per minute versus typing at 40-60 wpm. That\'s a 3-5x productivity boost!',
    category: 'general',
  },
  {
    question: 'Is it suitable for professional writing?',
    answer: 'Yes! SpeakFlow is used by lawyers, doctors, writers, developers, and academics. It handles technical vocabulary, maintains formatting, and produces publication-ready text.',
    category: 'general',
  },
];

const categories = [
  { id: 'all', label: 'All Questions', icon: HelpCircle },
  { id: 'general', label: 'General', icon: HelpCircle },
  { id: 'technical', label: 'Technical', icon: HelpCircle },
  { id: 'pricing', label: 'Pricing', icon: HelpCircle },
  { id: 'features', label: 'Features', icon: HelpCircle },
];

export function FAQ() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [openItems, setOpenItems] = useState<number[]>([]);
  const sectionRef = useRevealOnScroll();

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <section id="faq" className="py-24 relative" ref={sectionRef as any}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4 scroll-fade-up" data-delay="0">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <HelpCircle className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">FAQ</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">
              Got Questions? We've Got Answers
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about SpeakFlow. Can't find what you're looking for? 
              Feel free to contact our support team.
            </p>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-12 scroll-fade-up" data-delay="100">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-all",
                  activeCategory === category.id
                    ? "bg-primary text-primary-foreground shadow-lg"
                    : "bg-card hover:bg-accent"
                )}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <Card
                key={index}
                className={cn(
                  "overflow-hidden transition-all duration-300 scroll-fade-up",
                  openItems.includes(index) && "shadow-lg"
                )}
                data-delay={`${200 + index * 50}`}
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full text-left p-6 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-semibold text-lg pr-2">{faq.question}</h3>
                    <ChevronDown 
                      className={cn(
                        "h-5 w-5 text-muted-foreground shrink-0 transition-transform duration-300",
                        openItems.includes(index) && "rotate-180"
                      )}
                    />
                  </div>
                </button>
                
                <div
                  className={cn(
                    "px-6 overflow-hidden transition-all duration-300",
                    openItems.includes(index) ? "max-h-96 pb-6" : "max-h-0"
                  )}
                >
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Contact CTA */}
          <Card className="mt-12 border-primary/20 bg-primary/5 scroll-scale" data-delay="300">
            <div className="p-8 text-center space-y-4">
              <MessageCircle className="h-12 w-12 text-primary mx-auto" />
              <h3 className="text-2xl font-bold">Still Have Questions?</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Our support team is here to help. Get answers within minutes, not hours.
              </p>
              <div className="flex flex-wrap gap-4 justify-center pt-4">
                <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
                  Contact Support
                </button>
                <button className="px-6 py-3 bg-card rounded-lg font-medium hover:bg-accent transition-colors">
                  Join Community
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}